import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import classnames from '../utils/classnames.tsx';
import { Slot } from '@radix-ui/react-slot';
import { useLayoutContext } from '../contexts/LayoutContext.tsx';

const SCROLL_KEY = 'secondary-nav-scroll';

const SecondaryNavigationSectionLabel = ({ children, className, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return (
        <div
            className={classnames(
                'text-sm text-content-medium px-3 py-2 md:not-first:mt-4',
                // in the mobile row the label collapses to a divider line between the sections
                'max-md:h-5 max-md:w-px max-md:shrink-0 max-md:overflow-hidden max-md:p-0 max-md:mx-1 max-md:text-transparent max-md:bg-line-weak max-md:first:hidden',
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
};

const SecondaryNavigationList = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return (
        <div
            className={classnames(
                'flex flex-row items-center gap-2 px-3 py-2 md:flex-col md:items-stretch md:gap-1 md:px-4 md:py-5',
                className
            )}
            {...props}
        />
    );
};

const SecondaryNavigationButton = ({
    active = false,
    className,
    asChild,
    ...props
}: React.ComponentPropsWithRef<'button'> & { active?: boolean; asChild?: boolean }) => {
    const Comp = asChild ? Slot : 'button';
    const ref = useRef<HTMLButtonElement>(null);
    const isMobile = useLayoutContext()?.isMobile ?? false;

    // on small screens the navigation is a horizontal row, so the active item has to be scrolled into view
    useEffect(() => {
        if (active && isMobile) {
            ref.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
        }
    }, [active, isMobile]);

    return (
        <Comp
            ref={ref}
            className={classnames(
                'relative flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-content-strong hover:text-content-strong h-9 transition-colors',
                'max-md:w-auto max-md:shrink-0 max-md:whitespace-nowrap max-md:h-8',
                'cursor-pointer hover:bg-surface-strong transition-[padding] ease-in-out duration-200',
                active &&
                    '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active',
                className
            )}
            data-active={active}
            {...props}
        />
    );
};

const SecondaryNavigation = ({
    className,
    onScroll,
    ...props
}: Omit<React.ComponentPropsWithRef<'nav'>, 'children'> & { children?: React.ReactNode }) => {
    const navRef = useRef<HTMLElement>(null);
    const layout = useLayoutContext();
    const isMobile = layout?.isMobile ?? false;

    useEffect(() => {
        if (isMobile) return;

        const saved = sessionStorage.getItem(SCROLL_KEY);
        if (saved && navRef.current) {
            navRef.current.scrollTop = parseInt(saved, 10);
        }
    }, [isMobile]);

    const handleScroll = (e: React.UIEvent<HTMLElement>) => {
        if (!isMobile) {
            sessionStorage.setItem(SCROLL_KEY, e.currentTarget.scrollTop.toString());
        }
        onScroll?.(e);
    };

    const nav = (
        <nav
            ref={navRef}
            onScroll={handleScroll}
            className={classnames(
                'flex flex-none flex-row w-full overflow-x-auto overflow-y-hidden bg-surface-weak border-solid border-b border-line-weak',
                'md:flex-col md:w-72 md:overflow-y-auto md:border-b-0 md:border-r',
                className
            )}
            {...props}
        />
    );

    // below the page header on small screens (see PageHeader's slot), beside the page on larger ones;
    // the slot element arrives in the same commit as the header, so waiting for it avoids a mount-then-move
    if (isMobile) {
        return layout?.subHeaderSlot ? createPortal(nav, layout.subHeaderSlot) : null;
    }

    return nav;
};

export default Object.assign(SecondaryNavigation, {
    SectionLabel: SecondaryNavigationSectionLabel,
    Button: SecondaryNavigationButton,
    List: SecondaryNavigationList,
});
